/**
 * One hop of a rabbit or a hopping bird, as a pose over the hop's time (critters.ts): a crouch, a ballistic arc and a
 * landing. Pure — no three.js — so the curve is unit-tested (test/critters.test.ts).
 *
 * The old critters slid along a sine arc with no weight to them: the body never changed shape, the ground under the
 * arc was the cell centre's rather than the point's, and a whole cell went by in one 0.45 s bound — from the play
 * camera it read as floating (Kevin, 2026-09-23). A real rabbit crouches, launches stretched, lands squashed and
 * covers a body length or two a bound; the shares below give it that shape.
 */
export const HOP = {
  /** Share of the hop spent crouching before take-off (anticipation). */
  crouch: 0.18,
  /** Share of the hop spent absorbing the landing. */
  land: 0.22,
  /** Squash at the bottom of the crouch and the landing: the posture's y scale is 1 − this (x/z keep the volume). */
  squash: 0.2,
  /** Share of the crouch's squash still held at take-off, blended into the launch stretch over `launchBlend`. */
  crouchHold: 0.8,
  /** Stretch at take-off: y scale 1 + this, relaxing to neutral by the apex. */
  stretch: 0.16,
  /** Share of the air time over which the crouch's squash springs into the launch stretch. */
  launchBlend: 0.12,
  /** Nose-up at take-off and nose-down into the landing, radians (positive is nose down, as three's +x rotation). */
  pitch: 0.32,
  /** Share of `pitch` the head dips by at the bottom of the crouch. */
  crouchNod: 0.35,
  /** Share of the air time by which the hind legs, kicked out at take-off, are tucked again. */
  tuck: 0.45,
} as const;

export interface HopPose {
  /** Share of the hop's ground distance covered, 0 → 1, never going back. */
  along: number;
  /** Height of the feet above the ground, world units (0 while the feet are down). */
  lift: number;
  /** Posture stretch: y scale is 1 + squash (negative squashes). */
  squash: number;
  /** Posture pitch, radians (positive is nose down). */
  pitch: number;
  /** Hind-leg extension, 0 (tucked) → 1 (kicked out behind at take-off). */
  legs: number;
  /** True while the feet are off the ground. */
  airborne: boolean;
}

/** The pose at the bottom of the crouch, the instant before take-off. */
const CROUCHED = { squash: -HOP.squash * HOP.crouchHold, pitch: HOP.pitch * HOP.crouchNod } as const;

function smooth(u: number): number { return u * u * (3 - 2 * u); }

/** The pose `t` (0 → 1) of the way through a hop that peaks `height` world units up. */
export function hopPose(t: number, height: number, out: HopPose = { along: 0, lift: 0, squash: 0, pitch: 0, legs: 0, airborne: false }): HopPose {
  const u0 = Math.min(1, Math.max(0, t));
  const air0 = HOP.crouch, air1 = 1 - HOP.land;
  if (u0 < air0) {
    const u = u0 / HOP.crouch;
    out.along = 0; out.lift = 0; out.airborne = false;
    out.squash = CROUCHED.squash * Math.sin(u * Math.PI / 2);
    out.pitch = CROUCHED.pitch * u;                           // head dips as the haunches load
    out.legs = 0;
    return out;
  }
  if (u0 < air1) {
    const u = (u0 - air0) / (air1 - air0);
    out.along = u;                                            // ballistic: constant ground speed in the air
    out.lift = height * 4 * u * (1 - u);
    out.airborne = true;
    // The crouch's end pose springs into the launch over the first few frames rather than snapping.
    const k = u < HOP.launchBlend ? smooth(u / HOP.launchBlend) : 1;
    const squash = HOP.stretch * (1 - u) * (1 - u);
    const pitch = -HOP.pitch * (1 - 2 * u);                   // nose up leaving, nose down arriving
    const legs = Math.max(0, 1 - u / HOP.tuck);               // kicked out behind, tucked by the top of the arc
    out.squash = CROUCHED.squash + (squash - CROUCHED.squash) * k;
    out.pitch = CROUCHED.pitch + (pitch - CROUCHED.pitch) * k;
    out.legs = legs * k;
    return out;
  }
  const u = (u0 - air1) / HOP.land;
  out.along = 1; out.lift = 0; out.airborne = false;
  out.squash = -HOP.squash * Math.sin(u * Math.PI);
  out.pitch = HOP.pitch * (1 - u) * (1 - u);                  // settles back level from the nose-down arrival
  out.legs = 0;
  return out;
}
